<script setup>
import { ref, computed } from "vue";
import SearchableSelectComponent from "@/components/SearchableSelectComponent.vue";
import useActionButtonsCommon from "@/composables/ActionButtonsCommon";

const { store, loading, _error } = useActionButtonsCommon();

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(["update:modelValue"]);

const device_filter = computed(
  () => store.getters["devicesFilterOptions/getFilterOptions"]
);

const deviceInitialData = () => ({
  tag: "",
  mac: "",
  course_id: null,
  windtag_device_class_id: null,
});

const formData = ref(deviceInitialData());
const validationErrors = ref({
  tag: false,
  mac: false,
  course_id: false,
  windtag_device_class_id: false,
});
const isFormValidated = ref(false);

const courseNames = computed(() => device_filter.value?.courses || []);
const deviceClasses = computed(
  () => device_filter.value?.windtagDeviceClass || []
);

function closeModal() {
  _error.value = null;
  loading.value = false;
  emit("update:modelValue", false);
  resetValidation();
  clearFormData();
}

function validateField(fieldName) {
  const value = formData.value[fieldName];

  if (typeof value === "string") {
    validationErrors.value[fieldName] = value.trim() === "";
    return;
  }

  if (value == null) {
    validationErrors.value[fieldName] = true;
    return;
  }

  validationErrors.value[fieldName] = Object.keys(value).length === 0;
}

function validateForm() {
  isFormValidated.value = true;

  Object.keys(validationErrors.value).forEach((key) => {
    validateField(key);
  });

  return !Object.values(validationErrors.value).some((value) => value === true);
}

function resetValidation() {
  isFormValidated.value = false;
  Object.keys(validationErrors.value).forEach((key) => {
    validationErrors.value[key] = false;
  });
}

function clearFormData() {
  formData.value = deviceInitialData();
}

async function submitForm() {
  if (validateForm()) {
    const submissionData = { ...formData.value };

    if (submissionData.course_id && submissionData.course_id.id) {
      submissionData.course_id = submissionData.course_id.id;
    }

    if (
      submissionData.windtag_device_class_id &&
      submissionData.windtag_device_class_id.id
    ) {
      submissionData.windtag_device_class_id =
        submissionData.windtag_device_class_id.id;
    }

    const tagName = formData.value.tag;

    try {
      loading.value = true;
      await store.dispatch("windtagDevices/addWindtagDevice", submissionData);

      store.dispatch("toastNotification/displayToast", {
        message: `Device ${tagName} added successfully`,
        type: "success",
      });

      closeModal();
    } catch (error) {
      loading.value = false;
      _error.value = error?.message || "Failed to add device";

      store.dispatch("toastNotification/displayToast", {
        message: error?.message || "Failed to add device",
        type: "error",
      });
    }
  }
}
</script>

<template>
  <div
    class="modal fade"
    :class="{ 'show d-block': modelValue }"
    tabindex="-1"
    role="dialog"
    aria-labelledby="deviceModalLabel"
    :aria-hidden="!modelValue"
  >
    <div
      v-if="modelValue"
      @click="closeModal"
      class="modal-backdrop fade show"
    ></div>

    <div class="modal-dialog" role="document">
      <div class="modal-content bg-dark">
        <div class="modal-header">
          <h5 class="modal-title" id="deviceModalLabel">Add Device</h5>
          <button
            type="button"
            class="close text-white"
            @click="closeModal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitForm">
            <div class="form-group mb-3">
              <label for="tag">Tag Number</label>
              <input
                type="text"
                class="form-control bg-dark text-white"
                :class="{
                  'border-danger-validation':
                    isFormValidated && validationErrors.tag,
                }"
                id="tag"
                v-model="formData.tag"
                @input="validateField('tag')"
                placeholder="Enter Tag Number"
              />
              <small
                v-if="isFormValidated && validationErrors.tag"
                class="text-danger"
              >
                Tag Number is required
              </small>
            </div>

            <div class="form-group mb-3">
              <label for="mac">MAC</label>
              <input
                type="text"
                class="form-control bg-dark text-white"
                :class="{
                  'border-danger-validation':
                    isFormValidated && validationErrors.mac,
                }"
                id="mac"
                v-model="formData.mac"
                @input="validateField('mac')"
                placeholder="Enter MAC"
              />
              <small
                v-if="isFormValidated && validationErrors.mac"
                class="text-danger"
              >
                MAC is required
              </small>
            </div>

            <div class="form-group mb-3">
              <label for="courseName">Course Name</label>
              <SearchableSelectComponent
                v-model="formData.course_id"
                @update:modelValue="validateField('course_id')"
                :options="courseNames"
                :label="'name'"
                :trackBy="'id'"
                :placeholder="'Select Course Name'"
                :class="{
                  'error-select': isFormValidated && validationErrors.course_id,
                }"
              />
              <small
                v-if="isFormValidated && validationErrors.course_id"
                class="text-danger"
              >
                Course Name is required
              </small>
            </div>

            <div class="form-group mb-3">
              <label for="deviceClass">Device Class</label>
              <SearchableSelectComponent
                v-model="formData.windtag_device_class_id"
                @update:modelValue="validateField('windtag_device_class_id')"
                :options="deviceClasses"
                :label="'name'"
                :trackBy="'id'"
                :placeholder="'Select Device Class'"
                :class="{
                  'error-select':
                    isFormValidated && validationErrors.windtag_device_class_id,
                }"
              />
              <small
                v-if="
                  isFormValidated && validationErrors.windtag_device_class_id
                "
                class="text-danger"
              >
                Device Class is required
              </small>
            </div>

            <p v-if="_error" class="text-danger">
              {{ _error }}
            </p>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-danger" @click="closeModal">
            CLOSE
          </button>
          <button
            v-if="!loading"
            type="button"
            class="btn btn-success"
            @click="submitForm"
          >
            ADD DEVICE
          </button>
          <div
            v-if="loading"
            class="justify-content-center align-items-center"
            style="text-align: center"
          >
            <font-awesome-icon :icon="['fas', 'spinner']" spin size="2x" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import "@/styles/modal.css";
@import "@/styles/deviceform.css";
</style>
